import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Pagination from '../components/Pagination';
import usePagination from '../hooks/usePagination';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale';
import 'react-datepicker/dist/react-datepicker.css';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { DotsVerticalIcon, Pencil1Icon, TrashIcon } from '@radix-ui/react-icons';
import { formatDistance } from '../hooks/useGeolocation';
import './Global.css';
import './TimeRecordsManagement.css';

registerLocale('pt-BR', ptBR);

/* Localização gravada no ponto batido pelo app: link pro mapa, distância ao
   colégio e o veredito congelado no registro (near/far/unknown) como selo.
   O veredito é o do dia — mudar o raio depois não reescreve o passado. */
const GeoPin = ({ rotulo, lat, lng, acc, dist, match }) => {
  const precisao = acc != null ? `precisão ±${Math.round(acc)} m` : 'sem precisão informada';
  let cls = '';
  let sufixo = '';
  let title = `${rotulo}: ${lat}, ${lng} · ${precisao}`;
  if (dist != null) title += ` · a ${formatDistance(dist)} do colégio`;
  if (match === 'far') {
    cls = 'tr-geo-fora';
    sufixo = ' — fora do raio';
    title = `Registrado a ${formatDistance(dist)} do colégio, fora do raio permitido (${precisao}). Abra o mapa para conferir.`;
  } else if (match === 'unknown') {
    cls = 'tr-geo-impreciso';
    sufixo = ' — a confirmar';
    title = `A ${formatDistance(dist)} do colégio, mas a ${precisao} não permite afirmar que está fora do raio.`;
  }
  return (
    <span className={cls} title={title}>
      <a href={`https://www.google.com/maps?q=${lat},${lng}`} target="_blank" rel="noreferrer">
        {match === 'far' ? '⚠ ' : ''}📍 {rotulo}
        {dist != null && <span className="tr-geo-dist"> · {formatDistance(dist)}</span>}
        {sufixo}
      </a>
    </span>
  );
};

/* Códigos crus que a CGP devolve como motivo da glosa/pendência. A tela
   precisa traduzir: `aula_conflito_cod` não diz nada para quem lança. */
const MOTIVOS_CGP = {
  aula_conflito_cod: 'escala do COD',
  aula_em_ferias: 'de férias',
  aula_em_afastamento: 'afastado',
  aula_duplicada: 'duplicada',
  aula_sem_registro: 'sem registro',
};

const IMPEDIMENT_LABELS = {
  INACTIVE: 'Cadastro inativo',
  LICENSE: 'Licença',
  SCHEDULE_CONFLICT: 'Conflito de escala de aula',
  SERVICE_CONFLICT: 'Escala de serviço (COD / IRSO)',
};

const CalendarIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const AlertIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const EmptyIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);

const TimeRecordsManagement = () => {
  // Militar exibe posto/graduação; instrutor civil exibe o vínculo.
  const qualifierOf = (user) =>
    user?.personnelType === 'civil'
      ? user?.civilBond || 'Civil'
      : user?.militaryRank || '';

  const [records, setRecords] = useState([]);
  const [filteredRecords, setFilteredRecords] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  // Impedimento fica no modal, não só no toast: some antes de o usuário ler.
  const [impediment, setImpediment] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState(null);
  const [deletingRecord, setDeletingRecord] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedUserPositions, setSelectedUserPositions] = useState([]);

  /* Aba ativa. `todos` é a tela de sempre (recorte por data); `abertos` junta
     as entradas sem saída de QUALQUER data — o caso do fiscal que abriu o ponto
     e esqueceu de fechar, que ninguém enxerga porque o filtro nasce em "hoje".
     `?status=aberto` na URL cai direto nela (o dashboard linka pra cá). */
  const [searchParams, setSearchParams] = useSearchParams();
  const abertosNaURL = searchParams.get('status') === 'aberto';
  const [activeTab, setActiveTab] = useState(abertosNaURL ? 'abertos' : 'todos');

  // Filtros com Date objects
  const [filterStartDate, setFilterStartDate] = useState(abertosNaURL ? null : new Date());
  const [filterEndDate, setFilterEndDate] = useState(abertosNaURL ? null : new Date());
  // Só registros com hora retida acima do teto (saidaReal preenchida).
  const [somenteExcedente, setSomenteExcedente] = useState(false);

  const [formData, setFormData] = useState({
    userId: '',
    date: '',
    entryTime: '',
    exitTime: '',
    observation: '',
    reason: '',
    positionId: '',
  });

  const [modalDate, setModalDate] = useState(new Date());
  const [userSearch, setUserSearch] = useState('');
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Paginação padrão (mesmo hook das demais tabelas do sistema).
  const pagination = usePagination(filteredRecords, 10);

  // Helpers
  const toISODate = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDecimalToHours = (decimal) => {
    if (!decimal && decimal !== 0) return '0h 00min';
    const hours = Math.floor(decimal);
    const minutes = Math.round((decimal - hours) * 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}min`;
  };

  /* Quantos dias o registro está sem saída. Comparação em UTC para o horário
     de verão não deslocar a conta em um dia. */
  const hoje = toISODate(new Date());

  const diasEmAberto = (isoDate) => {
    const [y, m, d] = String(isoDate).split('T')[0].split('-').map(Number);
    const [hy, hm, hd] = hoje.split('-').map(Number);
    return Math.round((Date.UTC(hy, hm - 1, hd) - Date.UTC(y, m - 1, d)) / 86400000);
  };

  const labelDiasEmAberto = (dias) => {
    if (dias <= 0) return 'hoje';
    if (dias === 1) return '1 dia';
    return `${dias} dias`;
  };

  // 3 dias sem fechar já é risco de o mês virar sem a hora paga.
  const classeDiasEmAberto = (dias) => {
    if (dias >= 3) return 'tr-aberto-critico';
    if (dias >= 1) return 'tr-aberto-atrasado';
    return 'tr-aberto-hoje';
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    filterRecords();
  }, [records, filterStartDate, filterEndDate, activeTab, somenteExcedente]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-search-container')) {
        setShowUserDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserDropdown]);

  const loadData = async () => {
    try {
      const [recordsRes, usersRes] = await Promise.all([
        api.get('/time-records'),
        api.get('/users'),
      ]);
      setRecords(recordsRes.data);
      setUsers(usersRes.data.filter(u => u.isActive));
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const filterRecords = () => {
    const startISO = filterStartDate ? toISODate(filterStartDate) : '';
    const endISO = filterEndDate ? toISODate(filterEndDate) : '';
    const somenteAbertos = activeTab === 'abertos';

    const filtered = records.filter(record => {
      if (somenteAbertos && record.exitTime) return false;
      if (somenteExcedente && !record.saidaReal) return false;
      const recordDate = record.date.split('T')[0];
      const start = startISO || '0000-01-01';
      const end = endISO || '9999-12-31';
      return recordDate >= start && recordDate <= end;
    });

    // Em aberto: o mais antigo primeiro, que é o mais perto de virar mês
    // fechado sem hora paga. Nas outras abas, o mais recente primeiro.
    filtered.sort((a, b) => (somenteAbertos
      ? new Date(a.date) - new Date(b.date)
      : new Date(b.date) - new Date(a.date)));
    setFilteredRecords(filtered);
    pagination.setPage(1);
  };

  const handleChangeTab = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    if (tab === 'abertos') {
      // Pendência de saída quase nunca é de hoje: entrar na aba com o filtro
      // travado em "hoje" mostraria zero e esconderia justamente o problema.
      setFilterStartDate(null);
      setFilterEndDate(null);
      setSearchParams({ status: 'aberto' }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  /* Badge da aba: conta sobre TODOS os registros, não sobre o recorte de data
     — senão o alerta desaparece justamente quando o filtro está estreito. */
  const openRecords = useMemo(() => records.filter(r => !r.exitTime), [records]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const dataToSend = { ...formData };

      if (!dataToSend.exitTime || dataToSend.exitTime.trim() === '') {
        delete dataToSend.exitTime;
      }
      if (!dataToSend.observation || dataToSend.observation.trim() === '') {
        delete dataToSend.observation;
      }
      if (!dataToSend.reason || dataToSend.reason.trim() === '') {
        delete dataToSend.reason;
      }
      if (!dataToSend.positionId) delete dataToSend.positionId;

      setImpediment(null);
      if (editingRecord) {
        await api.patch(`/time-records/${editingRecord.id}`, dataToSend);
        toast.success('Registro atualizado com sucesso!');
      } else {
        await api.post('/time-records', dataToSend);
        toast.success('Registro criado com sucesso!');
      }

      handleCloseModal();
      loadData();
    } catch (error) {
      console.error('Erro ao salvar registro:', error);
      const isImpediment = error.response?.status === 422 || error.response?.data?.error === 'ImpedimentDetected';
      const msg = error.response?.data?.message || error.message;
      if (isImpediment) {
        setImpediment({
          message: msg,
          items: error.response?.data?.impediments || [],
        });
        toast.error(msg, { duration: 8000, icon: '🚫' });
      } else {
        setImpediment(null);
        toast.error('Erro ao salvar: ' + msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditRecord = (record) => {
    setEditingRecord(record);

    // Get user's positions for the select
    const user = users.find(u => u.id === record.userId);
    setSelectedUserPositions(user?.positions || []);

    // Registro cortado pelo teto: exitTime é a saída PAGA (derivada); o que
    // o admin edita é a batida de verdade (saidaReal). Mandar a saída paga
    // de volta faria o backend tratá-la como horário novo e perder a real.
    const saida = record.saidaReal || record.exitTime;
    setFormData({
      userId: record.userId,
      date: record.date,
      entryTime: record.entryTime.substring(0, 5),
      exitTime: saida ? saida.substring(0, 5) : '',
      observation: record.observation || '',
      reason: '',
      positionId: record.positionId || record.position?.id || '',
    });

    const [year, month, day] = record.date.split('-');
    setModalDate(new Date(year, month - 1, day));

    if (user) {
      setUserSearch(`${user.name}${user.matricula ? ` - ${user.matricula}` : ''}`);
    }

    setShowModal(true);
  };

  const handleDeleteRecord = async () => {
    if (!recordToDelete) return;
    setDeletingRecord(true);
    try {
      await api.delete(`/time-records/${recordToDelete.id}`);
      toast.success('Registro excluído com sucesso!');
      loadData();
    } catch (error) {
      console.error('Erro ao excluir registro:', error);
      toast.error('Erro ao excluir: ' + (error.response?.data?.message || error.message));
    } finally {
      setDeletingRecord(false);
      setShowDeleteModal(false);
      setRecordToDelete(null);
    }
  };

  const handleCloseModal = () => {
    setImpediment(null);
    setShowModal(false);
    setEditingRecord(null);
    setFormData({
      userId: '',
      date: toISODate(new Date()),
      entryTime: '',
      exitTime: '',
      observation: '',
      reason: '',
      positionId: '',
    });
    setSelectedUserPositions([]);
    setUserSearch('');
    setShowUserDropdown(false);
    setModalDate(new Date());
  };

  const handleNewRecord = () => {
    handleCloseModal();
    setSelectedUserPositions([]);
    setShowModal(true);
  };

  const currentRecords = pagination.pageItems;
  const somenteAbertos = activeTab === 'abertos';

  if (loading) return (
    <>
      <Navbar />
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Carregando registros...</p>
      </div>
    </>
  );

  return (
    <>
      <Toaster position="top-right" />
      <Navbar />
      <div className="tr-container">
        {/* Header */}
        <div className="tr-header">
          <div className="tr-header-info">
            <h1>Registros de Horários</h1>
            <p className="tr-subtitle">
              <span className={`tr-count-badge ${somenteAbertos ? 'is-alerta' : ''}`}>
                {filteredRecords.length}
              </span>
              {somenteAbertos
                ? (filteredRecords.length === 1 ? 'entrada sem saída' : 'entradas sem saída')
                : (filteredRecords.length === 1 ? 'registro encontrado' : 'registros encontrados')}
            </p>
          </div>
          <button className="tr-btn-new" onClick={handleNewRecord}>
            <PlusIcon />
            Novo Registro
          </button>
        </div>

        {/* Abas */}
        <div className="tr-tabs" role="tablist" aria-label="Situação dos registros">
          <button
            type="button"
            role="tab"
            aria-selected={!somenteAbertos}
            className={`tr-tab ${!somenteAbertos ? 'is-active' : ''}`}
            onClick={() => handleChangeTab('todos')}
          >
            Todos os registros
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={somenteAbertos}
            className={`tr-tab tr-tab-alerta ${somenteAbertos ? 'is-active' : ''}`}
            onClick={() => handleChangeTab('abertos')}
          >
            Sem saída
            <span className="tr-tab-badge">{openRecords.length}</span>
          </button>
        </div>

        {somenteAbertos && (
          <div className="tr-aviso-abertos" role="status">
            <span className="tr-aviso-icon"><AlertIcon /></span>
            <div className="tr-aviso-texto">
              <strong>Entradas que ninguém fechou.</strong> São pontos abertos pelo
              fiscal e sem horário de saída — enquanto ficarem assim, não contam
              hora nenhuma. O filtro de data entra vazio nesta aba, de propósito,
              para não esconder pendência antiga; use <strong>Registrar Saída</strong>{' '}
              em cada linha para fechar.
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="tr-filters">
          <div className="tr-filters-row">
            <div className="tr-filter-group">
              <label className="tr-filter-label">Data Inicial</label>
              <div className="tr-datepicker-wrapper">
                <DatePicker
                  selected={filterStartDate}
                  onChange={(date) => setFilterStartDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  placeholderText="Selecione a data"
                  maxDate={filterEndDate || undefined}
                />
                <span className="tr-datepicker-icon"><CalendarIcon /></span>
              </div>
            </div>

            <div className="tr-filter-group">
              <label className="tr-filter-label">Data Final</label>
              <div className="tr-datepicker-wrapper">
                <DatePicker
                  selected={filterEndDate}
                  onChange={(date) => setFilterEndDate(date)}
                  dateFormat="dd/MM/yyyy"
                  locale="pt-BR"
                  placeholderText="Selecione a data"
                  minDate={filterStartDate || undefined}
                />
                <span className="tr-datepicker-icon"><CalendarIcon /></span>
              </div>
            </div>

            <div className="tr-filter-group tr-filter-check">
              <label className="tr-filter-label">Excedentes</label>
              <label className="tr-check" title="Registros com hora retida acima do teto do ciclo">
                <input
                  type="checkbox"
                  checked={somenteExcedente}
                  onChange={(e) => setSomenteExcedente(e.target.checked)}
                />
                <span>Só com excedente</span>
              </label>
            </div>

            <button
              className="tr-btn-clear"
              onClick={() => {
                setFilterStartDate(null);
                setFilterEndDate(null);
                setSomenteExcedente(false);
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="1 4 1 10 7 10" />
                <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
              </svg>
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-wrapper">
          <table className="modern-table tr-table">
            <thead>
              <tr>
                <th>Pessoa</th>
                <th>Função</th>
                <th>Data</th>
                <th>Entrada</th>
                {/* Sem saída, "Total Horas" é sempre zero e a CGP nunca conferiu:
                    as duas colunas dão lugar ao tempo que a pendência já tem. */}
                {somenteAbertos ? (
                  <th>Em aberto há</th>
                ) : (
                  <>
                    <th>Saída</th>
                    <th>Total Horas</th>
                    <th className="text-center">CGP</th>
                  </>
                )}
                <th>Observação</th>
                <th className="text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {currentRecords.length === 0 ? (
                <tr>
                  <td colSpan={somenteAbertos ? 7 : 9}>
                    <div className="tr-empty">
                      <div className="tr-empty-icon"><EmptyIcon /></div>
                      <p className="tr-empty-title">
                        {somenteAbertos ? 'Nenhuma entrada em aberto' : 'Nenhum registro encontrado'}
                      </p>
                      <p className="tr-empty-subtitle">
                        {somenteAbertos
                          ? 'Todo registro do período tem horário de saída lançado.'
                          : 'Ajuste os filtros de data ou crie um novo registro.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentRecords.map((record) => {
                  const isPending = !record.exitTime;
                  const user = users.find(u => u.id === record.userId);
                  return (
                    <tr key={record.id} className={isPending ? 'tr-row-pending' : ''}>
                      <td>
                        <div className="tr-user-name">{record.user?.name || '-'}</div>
                        {qualifierOf(user || record.user) && (
                          <div className="tr-user-rank">{qualifierOf(user || record.user)}</div>
                        )}
                      </td>
                      <td>{record.position?.name || '-'}</td>
                      <td className="col-time">{record.date.split('-').reverse().join('/')}</td>
                      <td>
                        <span className="tr-time">
                          {record.entryTime ? record.entryTime.substring(0, 5) : '-'}
                        </span>
                      </td>
                      {somenteAbertos ? (
                        <td>
                          {(() => {
                            const dias = diasEmAberto(record.date);
                            return (
                              <span
                                className={`tr-badge tr-badge-aberto ${classeDiasEmAberto(dias)}`}
                                title={dias >= 3
                                  ? 'Pendência antiga: confirme a saída com o fiscal antes de fechar o mês'
                                  : 'Entrada sem horário de saída'}
                              >
                                <span className="tr-badge-dot" />
                                {labelDiasEmAberto(dias)}
                              </span>
                            );
                          })()}
                        </td>
                      ) : (
                        <>
                        <td>
                          {isPending ? (
                            <span className="tr-badge tr-badge-pending">
                              <span className="tr-badge-dot" />
                              Pendente
                            </span>
                          ) : (
                            <>
                              <span className="tr-time">{record.exitTime.substring(0, 5)}</span>
                              {/* Registro ajustado pelo teto: a saída mostrada é a pagável;
                                  a batida de verdade fica em saidaReal. */}
                              {record.saidaReal && (
                                <span
                                  className="tr-retido"
                                  title={`Passou do teto do ciclo: ${formatDecimalToHours(record.horasExcedentes)} retidas. Saída real ${record.saidaReal.substring(0, 5)}; a CGP recebe só o pagável.`}
                                >
                                  retido {formatDecimalToHours(record.horasExcedentes)} · saída real{' '}
                                  {record.saidaReal.substring(0, 5)}
                                </span>
                              )}
                            </>
                          )}
                        </td>
                        <td>
                          {isPending ? (
                            <span className="tr-badge tr-badge-pending">
                              <span className="tr-badge-dot" />
                              Aguardando
                            </span>
                          ) : (
                            <span className="tr-total-hours">{formatDecimalToHours(record.totalHours)}</span>
                          )}
                        </td>
                        <td className="text-center">
                          {record.cgpGlosada ? (
                            <span
                              className="tr-badge tr-badge-glosa"
                              title={record.cgpObservacao || MOTIVOS_CGP[record.cgpMotivo] || record.cgpMotivo || ''}
                            >
                              Glosado
                              {record.cgpMotivo ? `: ${MOTIVOS_CGP[record.cgpMotivo] || record.cgpMotivo}` : ''}
                            </span>
                          ) : record.cgpPendencia ? (
                            <span
                              className="tr-badge tr-badge-pendencia"
                              title={`A CGP abriu pendência: ${MOTIVOS_CGP[record.cgpPendencia] || record.cgpPendencia}`}
                            >
                              Pendência
                            </span>
                          ) : record.cgpVerificadoEm ? (
                            <span className="tr-badge tr-badge-ok" title="A CGP conferiu e aceitou">
                              OK
                            </span>
                          ) : (
                            <span className="tr-cgp-vazio" title="A CGP ainda não importou este lançamento">—</span>
                          )}
                        </td>
                        </>
                      )}
                      <td>
                        <div className="tr-observation" title={record.observation || ''}>
                          {record.observation || '-'}
                        </div>
                        {/* Registro batido pelo próprio militar: link pro mapa da localização gravada */}
                        {(record.entryLatitude != null || record.exitLatitude != null) && (
                          <div className="tr-geo">
                            {record.entryLatitude != null && (
                              <GeoPin
                                rotulo="Entrada"
                                lat={record.entryLatitude}
                                lng={record.entryLongitude}
                                acc={record.entryGeoAccuracy}
                                dist={record.entryDistanceMeters}
                                match={record.entryLocationMatch}
                              />
                            )}
                            {record.exitLatitude != null && (
                              <GeoPin
                                rotulo="Saída"
                                lat={record.exitLatitude}
                                lng={record.exitLongitude}
                                acc={record.exitGeoAccuracy}
                                dist={record.exitDistanceMeters}
                                match={record.exitLocationMatch}
                              />
                            )}
                          </div>
                        )}
                      </td>
                      <td className="cell-actions">
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                          {isPending && (
                            <button
                              className="tr-btn-edit tr-btn-edit-pending"
                              onClick={() => handleEditRecord(record)}
                            >
                              Registrar Saída
                            </button>
                          )}
                          <DropdownMenu.Root>
                            <DropdownMenu.Trigger asChild>
                              <button className="icon-button" aria-label="Ações">
                                <DotsVerticalIcon />
                              </button>
                            </DropdownMenu.Trigger>
                            <DropdownMenu.Portal>
                              <DropdownMenu.Content className="dropdown-content" sideOffset={5}>
                                <DropdownMenu.Item className="dropdown-item" onSelect={() => handleEditRecord(record)}>
                                  <Pencil1Icon /> Editar
                                </DropdownMenu.Item>
                                <DropdownMenu.Separator className="dropdown-separator" />
                                <DropdownMenu.Item className="dropdown-item dropdown-item-danger" onSelect={() => {
                                  setRecordToDelete(record);
                                  setShowDeleteModal(true);
                                }}>
                                  <TrashIcon /> Excluir
                                </DropdownMenu.Item>
                              </DropdownMenu.Content>
                            </DropdownMenu.Portal>
                          </DropdownMenu.Root>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          id="time-records"
          page={pagination.page}
          totalPages={pagination.totalPages}
          total={pagination.total}
          from={pagination.from}
          to={pagination.to}
          pageSize={pagination.pageSize}
          onPageChange={pagination.setPage}
          onPageSizeChange={pagination.setPageSize}
          itemLabel="registros"
        />

        {/* Modal */}
        {showModal && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal tr-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingRecord ? 'Editar Registro' : 'Novo Registro'}</h2>
                <button className="modal-close" onClick={handleCloseModal}>×</button>
              </div>

              <form onSubmit={handleSubmit} className="form">
                {impediment && (
                  <div className="impediment-alert" role="alert">
                    <div className="impediment-alert__icon">🚫</div>
                    <div className="impediment-alert__body">
                      <div className="impediment-alert__title">Lançamento bloqueado</div>
                      {impediment.items.length > 0 ? (
                        <ul className="impediment-alert__list">
                          {impediment.items.map((imp, i) => (
                            <li key={i}>
                              <strong>{IMPEDIMENT_LABELS[imp.kind] || 'Impedimento'}</strong>
                              {imp.detail ? <> — {imp.detail}</> : null}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <div className="impediment-alert__list">{impediment.message}</div>
                      )}
                      <div className="impediment-alert__hint">
                        Verifique a pessoa e a data. Se a licença já terminou, atualize o
                        cadastro em <strong>Licenças</strong> antes de lançar.
                      </div>
                    </div>
                    <button
                      type="button"
                      className="impediment-alert__close"
                      onClick={() => setImpediment(null)}
                      aria-label="Fechar aviso"
                    >×</button>
                  </div>
                )}

                {/* User search */}
                <div className="form-group">
                  <label>Pessoa *</label>
                  <div className="user-search-container" style={{ position: 'relative' }}>
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setShowUserDropdown(true);
                      }}
                      onFocus={() => setShowUserDropdown(true)}
                      placeholder="Digite para buscar..."
                      required={!formData.userId}
                    />
                    {showUserDropdown && (
                      <div className="tr-user-dropdown">
                        {users
                          .filter(user => {
                            const s = userSearch.toLowerCase();
                            return (
                              user.name?.toLowerCase().includes(s) ||
                              user.matricula?.toLowerCase().includes(s) ||
                              user.email?.toLowerCase().includes(s)
                            );
                          })
                          .map((user) => (
                            <div
                              key={user.id}
                              className="tr-user-option"
                              onClick={() => {
                                const selectedUser = users.find(u => u.id === user.id);
                                const userPositions = selectedUser?.positions || [];
                                setSelectedUserPositions(userPositions);
                                const autoPositionId = userPositions.length === 1 ? userPositions[0].id : '';
                                setFormData({
                                  ...formData,
                                  userId: user.id,
                                  positionId: autoPositionId,
                                });
                                setUserSearch(`${user.name}${user.matricula ? ` - ${user.matricula}` : ''}`);
                                setShowUserDropdown(false);
                              }}
                            >
                              <div className="tr-user-option-name">{user.name}</div>
                              <div className="tr-user-option-info">
                                {user.matricula && `Matrícula: ${user.matricula}`}
                                {user.matricula && qualifierOf(user) && ' · '}
                                {qualifierOf(user)}
                              </div>
                            </div>
                          ))}
                        {users.filter(user => {
                          const s = userSearch.toLowerCase();
                          return (
                            user.name?.toLowerCase().includes(s) ||
                            user.matricula?.toLowerCase().includes(s) ||
                            user.email?.toLowerCase().includes(s)
                          );
                        }).length === 0 && (
                          <div className="tr-user-no-results">Nenhuma pessoa encontrada</div>
                        )}
                      </div>
                    )}
                  </div>
                  {formData.userId && <input type="hidden" name="userId" value={formData.userId} required />}
                </div>

                {selectedUserPositions.length > 1 && (
                  <div className="form-group">
                    <label>Função para este registro *</label>
                    <select
                      value={formData.positionId}
                      onChange={(e) => setFormData({ ...formData, positionId: e.target.value })}
                      required
                    >
                      <option value="">Selecione a função...</option>
                      {selectedUserPositions.map(pos => (
                        <option key={pos.id} value={pos.id}>{pos.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Date */}
                <div className="form-group">
                  <label>Data *</label>
                  <div className="tr-datepicker-wrapper">
                    <DatePicker
                      selected={modalDate}
                      onChange={(date) => {
                        setModalDate(date);
                        setFormData({ ...formData, date: toISODate(date) });
                      }}
                      dateFormat="dd/MM/yyyy"
                      locale="pt-BR"
                      required
                    />
                    <span className="tr-datepicker-icon"><CalendarIcon /></span>
                  </div>
                </div>

                {/* Times */}
                <div className="tr-form-row">
                  <div className="form-group">
                    <label>Hora de Entrada *</label>
                    <input
                      type="text"
                      value={formData.entryTime}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length >= 2) {
                          const hours = parseInt(value.slice(0, 2));
                          if (hours > 23) value = '23' + value.slice(2);
                          value = value.slice(0, 2) + ':' + value.slice(2);
                        }
                        if (value.length > 5) {
                          const minutes = parseInt(value.slice(3, 5));
                          if (minutes > 59) value = value.slice(0, 3) + '59';
                          else value = value.slice(0, 5);
                        }
                        setFormData({ ...formData, entryTime: value.slice(0, 5) });
                      }}
                      placeholder="HH:MM"
                      maxLength={5}
                      required
                      style={{ fontFamily: "'SF Mono', 'Fira Code', monospace" }}
                    />
                    <small className="tr-form-hint">Formato 24h (ex: 07:00)</small>
                  </div>

                  <div className="form-group">
                    <label>
                      Hora de Saída
                      {!editingRecord && <span style={{ fontWeight: 'normal', color: '#9ca3af', fontSize: '12px', marginLeft: '6px' }}>(opcional)</span>}
                    </label>
                    <input
                      type="text"
                      value={formData.exitTime}
                      onChange={(e) => {
                        let value = e.target.value.replace(/\D/g, '');
                        if (value.length >= 2) {
                          const hours = parseInt(value.slice(0, 2));
                          if (hours > 23) value = '23' + value.slice(2);
                          value = value.slice(0, 2) + ':' + value.slice(2);
                        }
                        if (value.length > 5) {
                          const minutes = parseInt(value.slice(3, 5));
                          if (minutes > 59) value = value.slice(0, 3) + '59';
                          else value = value.slice(0, 5);
                        }
                        setFormData({ ...formData, exitTime: value.slice(0, 5) });
                      }}
                      placeholder="HH:MM"
                      maxLength={5}
                      style={{ fontFamily: "'SF Mono', 'Fira Code', monospace" }}
                    />
                    <small className="tr-form-hint">
                      {!editingRecord
                        ? 'Deixe em branco para registrar apenas a entrada'
                        : editingRecord.saidaReal
                          ? `Saída real. A saída paga (${editingRecord.exitTime.substring(0, 5)}) é derivada do teto do ciclo e refeita ao salvar.`
                          : 'Formato 24h (ex: 18:00)'}
                    </small>
                  </div>
                </div>

                {/* Observation */}
                <div className="form-group">
                  <label>Observação</label>
                  <textarea
                    value={formData.observation}
                    onChange={(e) => setFormData({ ...formData, observation: e.target.value })}
                    rows={3}
                    placeholder="Observações sobre este registro..."
                  />
                </div>

                {/* Reason */}
                <div className="form-group">
                  <label>Motivo/Justificativa</label>
                  <input
                    type="text"
                    value={formData.reason}
                    onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                    placeholder="Ex: Registro retroativo, ajuste, etc."
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn-primary" disabled={submitting}>
                    {submitting ? 'Salvando...' : editingRecord ? 'Salvar Alterações' : 'Criar Registro'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && recordToDelete && (
          <div className="modal-overlay" onClick={() => !deletingRecord && (setShowDeleteModal(false), setRecordToDelete(null))}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Excluir registro</h2>
                <button type="button" className="modal-close" onClick={() => { setShowDeleteModal(false); setRecordToDelete(null); }} disabled={deletingRecord}>
                  ×
                </button>
              </div>
              <div className="confirmation-body">
                <p>
                  Tem certeza que deseja excluir o registro de{' '}
                  <strong>{recordToDelete.user?.name}</strong> do dia{' '}
                  <strong>{recordToDelete.date}</strong>?
                </p>
                <p className="warning-text">
                  Essa ação é permanente e será registrada nos logs de auditoria.
                </p>
              </div>
              <div className="modal-actions" style={{ padding: '0 24px 24px' }}>
                <button type="button" className="btn-secondary" onClick={() => { setShowDeleteModal(false); setRecordToDelete(null); }} disabled={deletingRecord}>
                  Cancelar
                </button>
                <button type="button" className="btn-danger" onClick={handleDeleteRecord} disabled={deletingRecord}>
                  {deletingRecord ? 'Removendo...' : 'Sim, excluir'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default TimeRecordsManagement;
